import { useEffect, useRef } from 'react';

interface UseArrowKeyNavigationOptions {
	onArrowLeft?: () => void;
	onArrowRight?: () => void;
	enabled?: boolean;
}

const EDITABLE_ELEMENT_SELECTOR =
	'input, textarea, select, [contenteditable="true"], [role="combobox"]';

const useArrowKeyNavigation = ({
	onArrowLeft,
	onArrowRight,
	enabled = true,
}: UseArrowKeyNavigationOptions): void => {
	const onArrowLeftRef = useRef(onArrowLeft);
	const onArrowRightRef = useRef(onArrowRight);
	onArrowLeftRef.current = onArrowLeft;
	onArrowRightRef.current = onArrowRight;

	useEffect(() => {
		if (!enabled) {
			return undefined;
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (
				event.defaultPrevented ||
				event.metaKey ||
				event.ctrlKey ||
				event.altKey ||
				event.shiftKey
			) {
				return;
			}

			const target = event.target;
			if (target instanceof HTMLElement && target.closest(EDITABLE_ELEMENT_SELECTOR)) {
				return;
			}

			const hasVisibleModal = document.querySelector(
				'.ant-modal-wrap:not([style*="display: none"])'
			);
			if (
				hasVisibleModal ||
				document.querySelector('.ant-drawer-open') ||
				document.querySelector('.react-images__blanket') ||
				// AntD keeps a closed dropdown's overlay mounted in the DOM and marks it
				// with `-hidden` instead of removing it, so this must exclude that class
				// rather than just checking for `.ant-dropdown`'s presence.
				document.querySelector('.ant-dropdown:not(.ant-dropdown-hidden)')
			) {
				return;
			}

			if (event.key === 'ArrowLeft' && onArrowLeftRef.current) {
				event.preventDefault();
				onArrowLeftRef.current();
				return;
			}

			if (event.key === 'ArrowRight' && onArrowRightRef.current) {
				event.preventDefault();
				onArrowRightRef.current();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [enabled]);
};

export default useArrowKeyNavigation;
